import React from 'react';
import Icon from './Icon';
import { cap, fmtDateTime } from './DetailParts';
import { ServiceOrder } from '../api/orders';

/* Shared by the Legal Services Request and Service Order detail pages. */

// Same wording as the mobile app's booking screens, e.g. "Afternoon (12pm–5pm)".
const TIME_SLOT_LABEL: Record<NonNullable<ServiceOrder['timeSlot']>, string> = {
  morning: 'Morning (8am–12pm)',
  afternoon: 'Afternoon (12pm–5pm)',
  evening: 'Evening (5pm–8pm)',
};

export const fmtTimeSlot = (v?: ServiceOrder['timeSlot']): string =>
  v ? TIME_SLOT_LABEL[v] || cap(v) : '—';

/** Order progress stepper. */
export const OrderTimeline: React.FC<{ timeline: NonNullable<ServiceOrder['timeline']> }> = ({
  timeline,
}) =>
  timeline.length ? (
    <ol style={{ listStyle: 'none', margin: 0, padding: 0 }}>
      {timeline.map((t, i) => {
        /*
         * The API sends the whole stepper, including stages the order has
         * NOT reached (state 'pending', at null). Those must never look
         * like completed history — a just-submitted request would read as
         * quoted, accepted and completed.
         */
        const pending = t.state === 'pending';
        const current = t.state === 'current';
        return (
          <li key={i} className="flex gap-2.5 py-1.5 items-start">
            <span style={{ paddingTop: 3, lineHeight: 0 }}>
              {pending ? (
                <span
                  style={{
                    display: 'inline-block',
                    width: 12,
                    height: 12,
                    borderRadius: 999,
                    border: '2px solid var(--g300)',
                    background: 'transparent',
                  }}
                />
              ) : (
                <Icon name="check-circle" size={14} color="var(--brand)" />
              )}
            </span>
            <div>
              <div
                className="text-sm"
                style={{
                  fontWeight: current ? 800 : 600,
                  color: pending ? 'var(--g400)' : 'var(--g700)',
                }}
              >
                {t.label || t.status}
                {current && (
                  <span
                    className="ml-2 text-[11px] font-bold uppercase tracking-wider"
                    style={{ color: 'var(--brand)' }}
                  >
                    Current
                  </span>
                )}
              </div>
              <div className="text-[12px]" style={{ color: 'var(--g500)' }}>
                {pending ? 'Not yet reached' : fmtDateTime(t.at)}
                {!pending && t.detail ? ` — ${t.detail}` : ''}
              </div>
            </div>
          </li>
        );
      })}
    </ol>
  ) : (
    <span className="text-sm text-g500">No progress recorded yet.</span>
  );
